import { useCallback, useEffect, useState } from "react";
import { Download, FileText, Loader2, Pencil, RotateCcw, Search, Trash2 } from "lucide-react";

const REASON_PRESETS = {
  employee: ["No longer with the company", "Wrong or incorrect entry",
             "Duplicate employee record", "Added by mistake"],
  default: ["Wrong or incorrect entry", "Duplicate entry", "Added by mistake",
            "No longer in use"],
};
import { api, apiError } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Field } from "@/pages/Leads";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

export default function RecordAdmin() {
  const [types, setTypes] = useState([]);
  const [entity, setEntity] = useState("inventory");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [bin, setBin] = useState([]);
  const [tab, setTab] = useState("records");
  const [action, setAction] = useState(null); // {mode, row, sub}
  const [form, setForm] = useState({ new_name: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : "";
      const [{ data: t }, { data: l }, { data: b }] = await Promise.all([
        api.get("/admin/records/types"),
        api.get(`/admin/records/${entity}${params}`),
        api.get("/admin/records/bin/list"),
      ]);
      setTypes(t.types);
      setRows(l.records);
      setBin(b.records);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [entity, search]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const run = async (fn, message) => {
    setBusy(true);
    try {
      const { data } = await fn();
      toast.success(message + (data?.relabelled ? ` • ${data.relabelled} linked record(s) updated` : ""));
      setAction(null);
      setForm({ new_name: "", reason: "" });
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const label = types.find((t) => t.key === entity)?.label || entity;

  return (
    <div data-testid="record-admin">
      <PageHeader title="Record Administration"
        subtitle="Rename or remove any master record. A reason is required, and a downloadable copy of the old record is always kept as proof." />

      <div className="p-4 sm:p-6">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="record-admin-tabs">
            <TabsTrigger value="records" data-testid="tab-records">Records</TabsTrigger>
            <TabsTrigger value="bin" data-testid="tab-bin">
              Bin & proof {bin.length ? `(${bin.length})` : ""}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="records" className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <Select value={entity} onValueChange={(v) => { setEntity(v); setSearch(""); }}>
                <SelectTrigger className="w-52" data-testid="entity-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {types.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="relative w-full max-w-xs">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input className="pl-9" data-testid="record-search-input" placeholder={`Search ${label.toLowerCase()}s`}
                  value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
            </div>

            {loading ? (
              <div className="flex h-32 items-center justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="space-y-2">
                {rows.map((r) => (
                  <div key={r.id} className="surface p-3" data-testid={`record-row-${r.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <span className="text-sm font-medium">{r.name}</span>
                        <div className="text-[11px] text-muted-foreground">
                          {Object.entries(r.extra || {}).map(([k, v]) => `${k}: ${v}`).join(" • ")
                            || label}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" className="h-7 gap-1 px-2 text-[11px]"
                          data-testid={`rename-${r.id}`}
                          onClick={() => { setAction({ mode: "rename", row: r }); setForm({ new_name: r.name, reason: "" }); }}>
                          <Pencil className="h-3 w-3" /> Rename
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 gap-1 px-2 text-[11px] text-destructive"
                          data-testid={`delete-${r.id}`}
                          onClick={() => { setAction({ mode: "delete", row: r }); setForm({ new_name: "", reason: "" }); }}>
                          <Trash2 className="h-3 w-3" /> Delete
                        </Button>
                      </div>
                    </div>
                    {r.subcategories?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {r.subcategories.map((s) => (
                          <Badge key={s} variant="secondary" className="gap-1.5 py-1 text-[11px]">
                            {s}
                            <button type="button" title="Rename sub-category"
                              data-testid={`rename-sub-${r.id}-${s}`}
                              onClick={() => { setAction({ mode: "rename", row: r, sub: s }); setForm({ new_name: s, reason: "" }); }}>
                              <Pencil className="h-2.5 w-2.5" />
                            </button>
                            <button type="button" title="Delete sub-category"
                              data-testid={`delete-sub-${r.id}-${s}`}
                              onClick={() => { setAction({ mode: "delete", row: r, sub: s }); setForm({ new_name: "", reason: "" }); }}>
                              <Trash2 className="h-2.5 w-2.5" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                {rows.length === 0 && (
                  <p className="py-8 text-center text-sm text-muted-foreground" data-testid="record-empty">
                    Nothing matches that search.
                  </p>
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="bin" className="space-y-2">
            <p className="text-[11px] text-muted-foreground">
              Every removal is kept here with its reason. Download the copy for your records, or
              put the item back. Linked documents and history were never touched.
            </p>
            {bin.map((b) => (
              <div key={b.id} className="surface p-3" data-testid={`bin-row-${b.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <span className="text-sm font-medium">{b.name}</span>
                    <Badge variant="outline" className="ml-2 text-[9px] uppercase">{b.entity}</Badge>
                    {b.restored_at && <Badge className="ml-1 text-[9px]">RESTORED</Badge>}
                    <div className="text-[11px] text-muted-foreground">
                      Reason: {b.reason}
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      by {b.deleted_by_name} • {String(b.deleted_at).slice(0, 16).replace("T", " ")}
                      {Object.keys(b.related || {}).length
                        ? ` • linked: ${Object.entries(b.related).map(([k, v]) => `${k} ${v}`).join(", ")}`
                        : ""}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" className="h-7 gap-1 px-2 text-[11px]"
                      data-testid={`download-${b.id}`}
                      onClick={async () => {
                        try {
                          const r = await api.get(`/admin/records/bin/${b.id}/download`,
                            { responseType: "blob" });
                          const url = URL.createObjectURL(r.data);
                          const a = document.createElement("a");
                          a.href = url;
                          a.download = `deleted-${b.name}-${b.id}.json`;
                          a.click();
                          URL.revokeObjectURL(url);
                          toast.success("Proof copy downloaded");
                        } catch (e) {
                          toast.error(apiError(e));
                        }
                      }}>
                      <Download className="h-3 w-3" /> Proof copy
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 gap-1 px-2 text-[11px]"
                      data-testid={`download-pdf-${b.id}`}
                      onClick={async () => {
                        try {
                          const r = await api.get(`/admin/records/bin/${b.id}/download-pdf`,
                            { responseType: "blob" });
                          const url = URL.createObjectURL(r.data);
                          const a = document.createElement("a");
                          a.href = url;
                          a.download = `deletion-proof-${b.name}-${b.id}.pdf`;
                          a.click();
                          URL.revokeObjectURL(url);
                          toast.success("Proof PDF downloaded");
                        } catch (e) {
                          toast.error(apiError(e));
                        }
                      }}>
                      <FileText className="h-3 w-3" /> Proof PDF
                    </Button>
                    {!b.restored_at && (
                      <Button size="sm" variant="outline" className="h-7 gap-1 px-2 text-[11px]"
                        data-testid={`restore-${b.id}`}
                        onClick={() => { setAction({ mode: "restore", row: b }); setForm({ new_name: "", reason: "" }); }}>
                        <RotateCcw className="h-3 w-3" /> Put back
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {bin.length === 0 && (
              <p className="py-8 text-center text-sm text-muted-foreground" data-testid="bin-empty">
                Nothing has been removed yet.
              </p>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={!!action} onOpenChange={(o) => !o && setAction(null)}>
        <DialogContent data-testid="record-action-dialog">
          <DialogHeader>
            <DialogTitle>
              {action?.mode === "rename" && `Rename ${action.sub || action.row?.name}`}
              {action?.mode === "delete" && `Delete ${action.sub || action.row?.name}`}
              {action?.mode === "restore" && `Put back ${action.row?.name}`}
            </DialogTitle>
          </DialogHeader>

          {action?.mode === "rename" && (
            <Field label="New name">
              <Input data-testid="new-name-input" value={form.new_name}
                onChange={(e) => setForm({ ...form, new_name: e.target.value })} />
            </Field>
          )}
          {action?.mode === "delete" && (
            <p className="rounded border border-amber-500/40 bg-amber-500/10 p-2.5 text-xs">
              A full copy is saved in the Bin with your reason, so you can download it as proof or
              put it back later. Quotations, invoices and transactions that mention it are not
              changed.
            </p>
          )}
          <Field label="Reason (required, kept as proof)">
            <Input data-testid="reason-input" placeholder="e.g. duplicate entry created by mistake"
              value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
          </Field>
          {action?.mode === "delete" && (
            <div className="flex flex-wrap gap-1.5" data-testid="reason-presets">
              {(REASON_PRESETS[entity] || REASON_PRESETS.default).map((p) => (
                <button key={p} type="button" data-testid={`reason-preset-${p.split(" ")[0].toLowerCase()}`}
                  className="rounded-full border px-2.5 py-1 text-[11px] hover:bg-accent"
                  onClick={() => setForm({ ...form, reason: p })}>{p}</button>
              ))}
            </div>
          )}

          <DialogFooter>
            <Button data-testid="record-action-confirm" disabled={busy}
              variant={action?.mode === "delete" ? "destructive" : "default"}
              onClick={() => {
                const { mode, row, sub } = action;
                if (mode === "rename") {
                  return run(() => api.put(`/admin/records/${entity}/${row.id}/rename`,
                    sub
                      ? { new_name: sub, subcategory: sub, new_subcategory: form.new_name, reason: form.reason }
                      : { new_name: form.new_name, reason: form.reason }), "Renamed");
                }
                if (mode === "delete") {
                  return run(() => api.post(`/admin/records/${entity}/${row.id}/delete`,
                    { reason: form.reason, subcategory: sub || "" }),
                    "Removed — a proof copy is in the Bin");
                }
                return run(() => api.post(`/admin/records/bin/${row.id}/restore`,
                  { reason: form.reason }), "Put back");
              }}>
              {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              {action?.mode === "delete" ? "Delete with reason" : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
